// ============================================================================
// OBT F2-A — Kampanya liste hook'u
// ============================================================================
// Mevcut {data, loading, error, refresh} deseni. react-query yok.
// Filtreler YALNIZ backend'in gerçekten desteklediği alanlardır (status,
// topic, only_late). Backend desteklemeyen filtre istemci tarafında tüm veriyi
// çekerek TAKLİT EDİLMEZ (talimat §4).
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { listCampaigns } from './campaignApi';
import { normalizeObtError } from '../obtErrors';

export const CAMPAIGN_PAGE_SIZE = 20;
// Yönetim listesi arka planda tazelenir: "Bilgi Talepleri" ekranında beklerken
// yeni cevap/talep manuel "Yenile" GEREKTİRMEDEN sayaçlara/karta yansır.
const POLL_MS = 45_000;

function isCanceled(error) {
  return (
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError'
  );
}

/**
 * @param {{enabled?: boolean}} options
 */
export function useCampaignList({ enabled = true } = {}) {
  const [filters, setFilters] = useState({
    status: '',
    topic: '',
    onlyLate: false,
    archived: false,
    deleted: false,
  });
  const [offset, setOffset] = useState(0);
  const [state, setState] = useState({
    loading: enabled,
    error: null,
    items: [],
    page: null,
  });
  const abortRef = useRef(null);

  // `silent`: arka plan yoklaması — spinner GÖSTERME (liste titremesin). İlk
  // yükleme, filtre/sayfa değişimi ve manuel "Yenile" silent=false.
  const load = useCallback(
    async (silent = false) => {
      if (!enabled) {
        setState({ loading: false, error: null, items: [], page: null });
        return;
      }
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      if (!silent) setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const data = await listCampaigns(
          {
            status: filters.status || undefined,
            topic: filters.topic || undefined,
            onlyLate: filters.onlyLate || undefined,
            archived: filters.archived || undefined,
            deleted: filters.deleted || undefined,
            limit: CAMPAIGN_PAGE_SIZE,
            offset,
          },
          { signal: controller.signal }
        );
        if (controller.signal.aborted) return;
        setState({
          loading: false,
          error: null,
          items: Array.isArray(data?.items) ? data.items : [],
          page: data?.page || null,
        });
      } catch (error) {
        if (isCanceled(error) || controller.signal.aborted) return;
        // Arka plan yoklaması başarısızsa mevcut listeyi KORU.
        if (!silent) {
          setState({
            loading: false,
            error: normalizeObtError(error),
            items: [],
            page: null,
          });
        }
      }
    },
    [enabled, filters, offset]
  );

  useEffect(() => {
    load();
    if (!enabled) return () => abortRef.current?.abort();
    const timer = setInterval(() => load(true), POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') load(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      abortRef.current?.abort();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load, enabled]);

  const applyFilters = useCallback((next) => {
    setOffset(0);
    setFilters((prev) => ({ ...prev, ...next }));
  }, []);

  // Manuel "Yenile" spinner'lı yükleme (tıklama olayı `silent`'e sızmasın).
  const reload = useCallback(() => load(false), [load]);

  return {
    ...state,
    filters,
    offset,
    setOffset,
    applyFilters,
    reload,
    pageSize: CAMPAIGN_PAGE_SIZE,
  };
}
